import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import {
	clearCookiePreferenceStorage,
	localStorageGetCookiePreferences,
	localStorageSetCookiePreferences,
	type CookiePreferencesV1,
} from '../localStorage';
import { updateGA4Consent } from '../common/analytics';
import { getIsLandingLoginRedirectPending } from '../hooks/useLandingLoginRedirect';

interface CookieConsentContextType {
	/** User finished the cookie drawer (essential acknowledged). */
	cookiePromptCompleted: boolean;
	/** GA4 / analytics measurement allowed */
	analyticsConsent: boolean;
	/**
	 * Intro “Continue” is allowed only after “Save preferences”, not after “Reject all” alone.
	 * Legacy storage without the flag is treated as allowed.
	 */
	cookiesAllowIntroContinue: boolean;
	/** Drawer visible: first visit, or user reopened it (e.g. from intro) before saving. */
	cookieDrawerOpen: boolean;
	/** Bumps when another surface asks to bring the cookie UI into view (e.g. intro “Review cookies”). */
	cookiePromptFocusNonce: number;
	requestCookiePromptFocus: () => void;
	saveCookiePreferences: (analytics: boolean, source?: 'save' | 'reject-all') => void;
	resetCookieConsent: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const useCookieConsent = () => {
	const context = useContext(CookieConsentContext);
	if (context === undefined) {
		throw new Error('useCookieConsent must be used within a CookieConsentProvider');
	}
	return context;
};

interface CookieConsentProviderProps {
	children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
	const [preferences, setPreferencesState] = useState<CookiePreferencesV1>(() => {
		const stored = localStorageGetCookiePreferences();
		return stored ?? { completed: false, analytics: false };
	});
	const [cookiePromptFocusNonce, setCookiePromptFocusNonce] = useState(0);
	const [cookieDrawerRequested, setCookieDrawerRequested] = useState(false);

	const cookiesAllowIntroContinue =
		preferences.completed && preferences.cookieChoiceConfirmedWithSave !== false;

	const cookieDrawerOpen =
		!getIsLandingLoginRedirectPending() &&
		(!preferences.completed || cookieDrawerRequested);

	const requestCookiePromptFocus = useCallback(() => {
		setCookieDrawerRequested(true);
		setCookiePromptFocusNonce((n) => n + 1);
	}, []);

	const saveCookiePreferences = (analytics: boolean, source: 'save' | 'reject-all' = 'save') => {
		const next: CookiePreferencesV1 = {
			completed: true,
			analytics,
			cookieChoiceConfirmedWithSave: source === 'save',
		};
		setPreferencesState(next);
		localStorageSetCookiePreferences(next);
		updateGA4Consent(analytics);
		setCookieDrawerRequested(false);
	};

	const resetCookieConsent = () => {
		clearCookiePreferenceStorage();
		setPreferencesState({ completed: false, analytics: false });
		setCookieDrawerRequested(false);
	};

	return (
		<CookieConsentContext.Provider value={{
			cookiePromptCompleted: preferences.completed,
			analyticsConsent: preferences.analytics,
			cookiesAllowIntroContinue,
			cookieDrawerOpen,
			cookiePromptFocusNonce,
			requestCookiePromptFocus,
			saveCookiePreferences,
			resetCookieConsent,
		}}>
			{children}
		</CookieConsentContext.Provider>
	);
};
